// components/portal/Floor3DView.tsx
'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text, PerspectiveCamera, OrthographicCamera, OrbitControls, Environment, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';
import { LeasingFloorDTO, LeasingUnitDTO } from '#types/types';
import { getUnitStatusHex } from './portalTheme';
import { useKernelStore } from '@/kernel/kernelStores';

interface Floor3DViewProps {
  floorId: string;
  focusedUnitIds?: string[];
  availableFloors?: LeasingFloorDTO[];
  onFloorChange?: (floor: LeasingFloorDTO) => void;
  /** 'cinematic3d' (default): animated orbit camera. 'static2d': fixed top-down view. */
  mode?: 'cinematic3d' | 'static2d';
  /** Optional CSS aspect ratio enforced on the root container (e.g. "16 / 9"). */
  enforcedAspectRatio?: string;
}

type TourCameraStateSnapshot = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov: number;
};

type FloorSceneProps = {
  floorId: string;
  focusedUnitIds?: string[];
};

// useShallow compares the resolved DTOs by reference, so a mutation on a unit of another floor
// leaves this array identical and the scene does not re-render.
const useFloorUnits = (floorId: string) =>
  useKernelStore(
    useShallow((state) => {
      const ids = state.inventory.Floor.registry[floorId]?.unitIds ?? [];
      return ids.map(id => state.inventory.Unit.registry[id]).filter(Boolean) as LeasingUnitDTO[];
    })
  );

const useFloorPlanFileId = (floorId: string) =>
  useKernelStore(state => state.inventory.Floor.registry[floorId]?.planFileId ?? null);

const calculatePolygonCentroid = (points: { x: number; y: number }[]) => {
  if (!points || points.length === 0) return { x: 0.5, y: 0.5, normalizedArea: 0 };
  let area = 0;
  let cx = 0;
  let cy = 0;
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const p1 = points[i]!;
    const p2 = points[(i + 1) % n]!;
    const f = p1.x * p2.y - p2.x * p1.y;
    area += f;
    cx += (p1.x + p2.x) * f;
    cy += (p1.y + p2.y) * f;
  }

  area *= 0.5;
  const absArea = Math.abs(area);
  if (absArea < 1e-10) return { ...points[0]!, normalizedArea: 0 }; 
  
  return {
    x: cx / (6 * area),
    y: cy / (6 * area),
    normalizedArea: absArea
  };
};

class UnitLogoErrorBoundary extends React.Component<React.PropsWithChildren<{ fallbackLabel: string; baseScale: number }>, { hasError: boolean }> {
  constructor(props: React.PropsWithChildren<{ fallbackLabel: string; baseScale: number }>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Text
          position={[0, 0, -0.01]}
          fontSize={this.props.baseScale * 0.3}
          color="black"
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          {this.props.fallbackLabel}
        </Text>
      );
    }
    return this.props.children;
  }
}

const UnitLogoInner = React.memo(({ url, baseScale }: { url: string; baseScale: number }) => {
  const texture = useTexture(url);

  const [width, height] = useMemo(() => {
    if (!texture?.image) return [baseScale, baseScale];
    const ratio = texture.image.width / texture.image.height || 1;

    if (ratio > 1) {
      return [baseScale, baseScale / ratio];
    } else {
      return [baseScale * ratio, baseScale];
    }
  }, [texture, baseScale]);

  return (
    <mesh position={[0, 0, 0.005]}>
      <planeGeometry args={[width, height]} />
      <meshBasicMaterial map={texture} transparent toneMapped={false} side={THREE.DoubleSide} />
    </mesh>
  );
});

const UnitLogo = React.memo(({ url, baseScale, fallbackLabel }: { url: string; baseScale: number; fallbackLabel: string }) => (
  <UnitLogoErrorBoundary fallbackLabel={fallbackLabel} baseScale={baseScale}>
    <UnitLogoInner url={url} baseScale={baseScale} />
  </UnitLogoErrorBoundary>
));

const ExtrudedUnit = React.memo(({ unitId, floorId, aspectRatio, isFocused }: { unitId: string; floorId: string; aspectRatio: number; isFocused: boolean }) => {
  const groupRef = useRef<THREE.Group>(null);

  const unit = useKernelStore(state => state.inventory.Unit.registry[unitId]);

  // 1. Résolution réactive de la marque via le store
  const brand = useKernelStore(state => {
    const activeDealId = state.inventory.Unit.registry[unitId]?.activeDealId;
    const deal = activeDealId ? state.inventory.Deal.registry[activeDealId] : null;
    return deal?.brandId ? state.inventory.Brand.registry[deal.brandId] : null;
  });

  const pos = unit?.positions?.find(p => p.floorId === floorId);

  // EDIT 4 START
  // Deterministic jitter based on ID to prevent Z-fighting between different units' labels
  const jitter = useMemo(() => {
    const hash = unitId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return (hash % 100) * 0.000025; // Up to 5cm of variation in height
  }, [unitId]);

  const shape = useMemo(() => {
    try {
      const s = new THREE.Shape();
      if (!pos?.geoJson?.coordinates || pos.geoJson.coordinates.length < 3) {
        return s;
      }
      pos.geoJson.coordinates.forEach((p, i) => {
        const x = (p.x - 0.5) * aspectRatio;
        const y = (0.5 - p.y); 
        if (i === 0) s.moveTo(x, y);
        else s.lineTo(x, y);
      });
      return s;
    } catch (err) {
      console.error(`Error creating shape for unit ${unit?.unitIdentifier}:`, err);
      return new THREE.Shape();
    }
  }, [pos, aspectRatio, unit?.unitIdentifier]);

  const color = useMemo(() => {
    if (isFocused) return '#3b82f6'; // White highlight for focused unit
    return getUnitStatusHex(unit?.status);
  }, [isFocused, unit?.status]);

  const { particleScale, particleHeight } = useMemo(() => {
    if (!pos?.geoJson?.coordinates) return { particleScale: 0.05, particleHeight: 0.07 };

    const { normalizedArea } = calculatePolygonCentroid(pos.geoJson.coordinates);
    const area3D = normalizedArea * aspectRatio;
    const characteristicLength = Math.sqrt(area3D);
    
    const baseScale = Math.max(0.035, Math.min(0.15, characteristicLength * 0.85));
    const roofLevel = 0.05; 
    const sizeOffset = (0.15 - baseScale) * 0.1; 
    
    return {
      particleScale: baseScale,
      particleHeight: roofLevel + 0.02 + sizeOffset + jitter
    };
  }, [pos?.geoJson, aspectRatio, jitter]);

  const centroidPos = useMemo(() => {
    if (!pos?.geoJson?.coordinates) return new THREE.Vector3(0, 0, 0);
    const { x, y } = calculatePolygonCentroid(pos.geoJson.coordinates);
    return new THREE.Vector3((x - 0.5) * aspectRatio, particleHeight, (y - 0.5));
  }, [pos?.geoJson, aspectRatio, particleHeight]);

  useFrame((state) => {
    if (!groupRef.current) return;
    const dx = state.camera.position.x - centroidPos.x;
    const dz = state.camera.position.z - centroidPos.z;
    const angle = Math.atan2(dx, dz);
    groupRef.current.rotation.z = angle;
  });

  if (!unit || !pos?.geoJson?.coordinates) return null;

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} receiveShadow renderOrder={1}>
        <extrudeGeometry args={[shape, { depth: 0.05, bevelEnabled: false }]} />
        <meshStandardMaterial color={color} metalness={0.2} roughness={0.8} />
      </mesh>

      <group 
        position={[centroidPos.x, centroidPos.y, centroidPos.z]} 
        rotation={[-Math.PI / 2, 0, 0]}
        renderOrder={10} 
      >
        <group ref={groupRef}>
          <React.Suspense fallback={null}>
            {brand?.logoUrl ? (
              <UnitLogo
                url={brand.logoUrl}
                baseScale={particleScale}
                fallbackLabel={brand.name || unit.unitIdentifier}
              />
            ) : (
              <Text 
                position={[0, 0, -0.01]} 
                fontSize={particleScale * 0.3} 
                color="black" 
                anchorX="center" 
                anchorY="middle" 
                fontWeight="bold"
              >
                {brand?.name || unit.unitIdentifier}
              </Text>
            )}
          </React.Suspense>
        </group>
      </group>
    </group>
  );
});

const CameraTour = ({
  pathPoints,
  centerPoint,
  unitPositions,
  onCameraStateUpdate
}: {
  pathPoints: THREE.Vector3[];
  centerPoint: THREE.Vector3;
  unitPositions: THREE.Vector3[];
  onCameraStateUpdate?: (snapshot: TourCameraStateSnapshot) => void;
}) => {
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const lookAtTarget = useRef(new THREE.Vector3().copy(centerPoint));
  
  const curve = useMemo(() => {
    if (pathPoints.length < 2) return null;
    return new THREE.CatmullRomCurve3(pathPoints, true, 'centripetal');
  }, [pathPoints]);
  
  const scroll = useRef(0);

  useFrame((state, delta) => {
    if (!cameraRef.current || !curve) return;
    
    scroll.current = (scroll.current + delta * 0.012) % 1; 
    const pos = curve.getPointAt(scroll.current);

    cameraRef.current.position.lerp(pos, 0.05);

    let closest = centerPoint;
    let minDistance = Infinity;
    
    unitPositions.forEach(uPos => {
      const dist = cameraRef.current!.position.distanceTo(uPos);
      if (dist < minDistance) {
        minDistance = dist;
        closest = uPos;
      }
    });

    const target = new THREE.Vector3().lerpVectors(centerPoint, closest, 0.6);
    lookAtTarget.current.lerp(target, 0.03);
    
    cameraRef.current.lookAt(lookAtTarget.current);
    onCameraStateUpdate?.({
      position: cameraRef.current.position,
      target: lookAtTarget.current,
      fov: cameraRef.current.fov
    });
  });

  return <PerspectiveCamera ref={cameraRef} makeDefault fov={35} />;
};

const ManualCamera = ({
  centerPoint,
  orbitRadius,
  initialState
}: {
  centerPoint: THREE.Vector3;
  orbitRadius: number;
  initialState: TourCameraStateSnapshot | null;
}) => {
  const startPosition = initialState?.position ?? new THREE.Vector3(
    centerPoint.x + orbitRadius * 0.7,
    orbitRadius * 0.8,
    centerPoint.z + orbitRadius * 0.7
  );
  const startTarget = initialState?.target ?? centerPoint;
  const startFov = initialState?.fov ?? 35;

  return (
    <>
      <PerspectiveCamera
        makeDefault
        fov={startFov}
        position={[startPosition.x, startPosition.y, startPosition.z]}
      />
      <OrbitControls
        target={startTarget}
        enableDamping
        dampingFactor={0.15}
        minDistance={orbitRadius * 0.3}
        maxDistance={orbitRadius * 3}
        maxPolarAngle={Math.PI / 2.1}
        minPolarAngle={0.2}
      />
    </>
  );
};

const FloorScene = ({
  floorId,
  focusedUnitIds,
  manualControlsEnabled,
  manualCameraStartState,
  onTourCameraStateUpdate
}: FloorSceneProps & {
  manualControlsEnabled: boolean;
  manualCameraStartState: TourCameraStateSnapshot | null;
  onTourCameraStateUpdate: (snapshot: TourCameraStateSnapshot) => void;
}) => {
  const units = useFloorUnits(floorId);
  const planFileId = useFloorPlanFileId(floorId);
  const assetUrl = `/api/inventory/assets/${planFileId}`;
  const texture = useTexture(assetUrl);

  const aspectRatio = useMemo(() => {
    return texture.image ? texture.image.width / texture.image.height : 1;
  }, [texture]);

  const { pathPoints, centerPoint, unitPositions, orbitRadius } = useMemo(() => {
    let targetCentroids: {x: number, y: number}[] = [];
    
    if (focusedUnitIds && focusedUnitIds.length > 0) {
      const targetUnits = units.filter(u => focusedUnitIds.includes(u.id));
      targetCentroids = targetUnits.map(u => {
        const pos = u.positions?.find(p => p.floorId === floorId);
        return pos?.geoJson?.meta?.centroid ?? (pos?.geoJson?.coordinates ? calculatePolygonCentroid(pos.geoJson.coordinates) : null);
      }).filter(Boolean) as {x: number, y: number}[];
    }

    if (targetCentroids.length === 0) {
      // Fallback to all units on floor
      targetCentroids = units.map(u => {
        const pos = u.positions?.find(p => p.floorId === floorId);
        return pos?.geoJson?.meta?.centroid ?? (pos?.geoJson?.coordinates ? calculatePolygonCentroid(pos.geoJson.coordinates) : null);
      }).filter(Boolean) as {x: number, y: number}[];
    }

    if (targetCentroids.length === 0) {
      return { 
        pathPoints: [new THREE.Vector3(1.5, 0.8, 1.5), new THREE.Vector3(-1.5, 0.8, 1.5), new THREE.Vector3(-1.5, 0.8, -1.5), new THREE.Vector3(1.5, 0.8, -1.5)],
        centerPoint: new THREE.Vector3(0, 0, 0),
        unitPositions: [],
        orbitRadius: 1.5
      };
    }

    let minX = 1, maxX = 0, minY = 1, maxY = 0;
    targetCentroids.forEach(c => {
      if (c.x < minX) minX = c.x;
      if (c.x > maxX) maxX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.y > maxY) maxY = c.y;
    });

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const center = new THREE.Vector3((centerX - 0.5) * aspectRatio, 0, (centerY - 0.5));

    const uPositions = targetCentroids.map(c => new THREE.Vector3((c.x - 0.5) * aspectRatio, 0, (c.y - 0.5)));

    const width = (maxX - minX) * aspectRatio;
    const depth = (maxY - minY);
    const maxDim = Math.max(width, depth);
    
    const isSingleUnit = targetCentroids.length === 1 && focusedUnitIds?.length === 1;
    const orbitRadius = isSingleUnit ? Math.max(0.4, maxDim * 1.4) : Math.max(0.6, maxDim * 1.3);
    const cameraHeight = isSingleUnit ? Math.max(0.5, maxDim * 1.1) : 0.8;

    const points = [];
    const segments = 16;
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(
        center.x + Math.cos(angle) * orbitRadius,
        cameraHeight,
        center.z + Math.sin(angle) * orbitRadius
      ));
    }

    return { pathPoints: points, centerPoint: center, unitPositions: uPositions, orbitRadius };
  }, [units, floorId, aspectRatio, focusedUnitIds]);

  return (
    <>
        {manualControlsEnabled ? (
          <ManualCamera
            centerPoint={centerPoint}
            orbitRadius={orbitRadius}
            initialState={manualCameraStartState}
          />
        ) : (
          <CameraTour
            pathPoints={pathPoints}
            centerPoint={centerPoint}
            unitPositions={unitPositions}
            onCameraStateUpdate={onTourCameraStateUpdate}
          />
        )}
        <ambientLight intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={1.5} castShadow />
        <spotLight position={[-10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
        
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow renderOrder={0}>
        <planeGeometry args={[aspectRatio, 1]} />
        <meshStandardMaterial map={texture} transparent opacity={0.8} roughness={1} />
        </mesh>

        {units.map(u => (
        <ExtrudedUnit key={u.id} unitId={u.id} floorId={floorId} aspectRatio={aspectRatio} isFocused={focusedUnitIds?.includes(u.id) ?? false} />
        ))}
        
        <Environment preset="city" />
    </>
  );
};

/** Static top-down 2D scene — no camera animation, lower GPU cost */
const Static2DScene = ({ floorId, focusedUnitIds }: FloorSceneProps) => {
  const units = useFloorUnits(floorId);
  const planFileId = useFloorPlanFileId(floorId);
  const assetUrl = `/api/inventory/assets/${planFileId}`;
  const texture = useTexture(assetUrl);

  const aspectRatio = useMemo(() => {
    return texture.image ? texture.image.width / texture.image.height : 1;
  }, [texture]);

  // Compute bounds to focus on targeted units
  const cameraZoom = useMemo(() => {
    if (!focusedUnitIds || focusedUnitIds.length === 0) return 1.8;
    return 2.2;
  }, [focusedUnitIds]);

  return (
    <>
      <OrthographicCamera
        makeDefault
        position={[0, 1, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        zoom={cameraZoom * 100}
        near={0.1}
        far={10}
      />
      <ambientLight intensity={1.2} />

      {/* Floor plan texture */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow renderOrder={0}>
        <planeGeometry args={[aspectRatio, 1]} />
        <meshBasicMaterial map={texture} transparent opacity={0.9} toneMapped={false} />
      </mesh>

      {/* Unit polygon overlays */}
      {units.map(u => {
        const pos = u.positions?.find(p => p.floorId === floorId);
        if (!pos?.geoJson?.coordinates || pos.geoJson.coordinates.length < 3) return null;
        const isFocused = focusedUnitIds?.includes(u.id) ?? false;
        const color = isFocused ? '#3b82f6' : getUnitStatusHex(u.status);

        const shape = new THREE.Shape();
        pos.geoJson.coordinates.forEach((p, i) => {
          const x = (p.x - 0.5) * aspectRatio;
          const y = (0.5 - p.y);
          if (i === 0) shape.moveTo(x, y);
          else shape.lineTo(x, y);
        });

        return (
          <mesh key={u.id} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]} renderOrder={1}>
            <shapeGeometry args={[shape]} />
            <meshBasicMaterial color={color} transparent opacity={isFocused ? 0.5 : 0.25} toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
    </>
  );
};

// R3F applies `gl` on every change of the prop's identity: a literal here reconfigures the
// renderer at each parent render instead of once at mount.
const CANVAS_GL_CONFIG = { antialias: true, alpha: true };

const FloorSelectorButton = React.memo(({
  floor,
  isActive,
  onSelect
}: {
  floor: LeasingFloorDTO;
  isActive: boolean;
  onSelect?: (floor: LeasingFloorDTO) => void;
}) => {
  const handleClick = useCallback(() => onSelect?.(floor), [onSelect, floor]);

  return (
    <button
      onClick={handleClick}
      className={clsx(
        'px-[1vmin] py-[0.4vmin] rounded-[0.5vmin] text-[1vmin] font-mono font-bold transition-all border',
        isActive
          ? 'bg-white-500 border-white-400 text-white shadow-[0_0_10px_rgba(246,246,246,0.4)]'
          : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white/80'
      )}
    >
      L{floor.level}
    </button>
  );
});

FloorSelectorButton.displayName = 'FloorSelectorButton';

export const Floor3DView = React.memo(({
  floorId,
  focusedUnitIds,
  availableFloors = [],
  onFloorChange,
  mode = 'cinematic3d',
  enforcedAspectRatio,
}: Floor3DViewProps) => {
  const floorExists = useKernelStore(state => !!state.inventory.Floor.registry[floorId]);
  const [manualControlsEnabled, setManualControlsEnabled] = useState(false);
  const [manualCameraStartState, setManualCameraStartState] = useState<TourCameraStateSnapshot | null>(null);
  const latestTourCameraStateRef = useRef<TourCameraStateSnapshot | null>(null);

  const handleTourCameraStateUpdate = useCallback((snapshot: TourCameraStateSnapshot) => {
    if (!latestTourCameraStateRef.current) {
      latestTourCameraStateRef.current = {
        position: new THREE.Vector3(),
        target: new THREE.Vector3(),
        fov: snapshot.fov
      };
    }
    latestTourCameraStateRef.current.position.copy(snapshot.position);
    latestTourCameraStateRef.current.target.copy(snapshot.target);
    latestTourCameraStateRef.current.fov = snapshot.fov;
  }, []);

  const enableManualControls = useCallback(() => {
    if (mode !== 'cinematic3d') return;
    if (manualControlsEnabled) return;
    const latest = latestTourCameraStateRef.current;
    setManualCameraStartState(
      latest
        ? {
            position: latest.position.clone(),
            target: latest.target.clone(),
            fov: latest.fov
          }
        : null
    );
    setManualControlsEnabled(true);
  }, [mode, manualControlsEnabled]);

  const rootStyle = useMemo(
    () => (enforcedAspectRatio ? { aspectRatio: enforcedAspectRatio } : undefined),
    [enforcedAspectRatio]
  );

  if (!floorExists) return null;

  return (
    <div
      className={clsx(
        'w-full bg-black/20 rounded-lg overflow-hidden border border-white/10 relative group/canvas',
        enforcedAspectRatio ? 'h-auto' : 'h-full'
      )}
      style={rootStyle}
    >
      {availableFloors.length > 1 && (
        <div className="absolute bottom-[1vmin] right-[1vmin] z-20 flex flex-col gap-[0.5vmin] items-end">
          <div className="flex flex-wrap justify-end gap-[0.4vmin] max-w-[20vmin]">
            {availableFloors.map((f) => (
              <FloorSelectorButton
                key={f.id}
                floor={f}
                isActive={f.id === floorId}
                onSelect={onFloorChange}
              />
            ))}
          </div>
        </div>
      )}

      <Canvas
        shadows={mode === 'cinematic3d'}
        gl={CANVAS_GL_CONFIG}
        onPointerDown={enableManualControls}
        onWheel={enableManualControls}
      >
        <React.Suspense fallback={null}>
          {mode === 'static2d' ? (
            <Static2DScene floorId={floorId} focusedUnitIds={focusedUnitIds} />
          ) : (
            <FloorScene
              floorId={floorId}
              focusedUnitIds={focusedUnitIds}
              manualControlsEnabled={manualControlsEnabled}
              manualCameraStartState={manualCameraStartState}
              onTourCameraStateUpdate={handleTourCameraStateUpdate}
            />
          )}
        </React.Suspense>
      </Canvas>
    </div>
  );
});

Floor3DView.displayName = 'Floor3DView';
